// Inline SVG icons copied verbatim from lucide-react so the Competencies tab can
// reproduce the prototype markup without pulling in a new icon dependency.
// Each path data matches the corresponding lucide icon at the time of copy.

type IconProps = {
    className?: string;
};

const base = (className?: string) => ({
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
});

export const ChevronDownIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <path d={'m6 9 6 6 6-6'} />
    </svg>
);

export const ChevronUpIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <path d={'m18 15-6-6-6 6'} />
    </svg>
);

export const MoreVerticalIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <circle
            cx={'12'}
            cy={'12'}
            r={'1'}
        />

        <circle
            cx={'12'}
            cy={'5'}
            r={'1'}
        />

        <circle
            cx={'12'}
            cy={'19'}
            r={'1'}
        />
    </svg>
);

export const PlusIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <path d={'M5 12h14'} />

        <path d={'M12 5v14'} />
    </svg>
);

export const SearchIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <circle
            cx={'11'}
            cy={'11'}
            r={'8'}
        />

        <path d={'m21 21-4.3-4.3'} />
    </svg>
);

export const UserPlusIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <path d={'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'} />

        <circle
            cx={'9'}
            cy={'7'}
            r={'4'}
        />

        <line
            x1={'19'}
            x2={'19'}
            y1={'8'}
            y2={'14'}
        />

        <line
            x1={'22'}
            x2={'16'}
            y1={'11'}
            y2={'11'}
        />
    </svg>
);

export const Trash2Icon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <path d={'M3 6h18'} />

        <path d={'M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6'} />

        <path d={'M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2'} />

        <line
            x1={'10'}
            x2={'10'}
            y1={'11'}
            y2={'17'}
        />

        <line
            x1={'14'}
            x2={'14'}
            y1={'11'}
            y2={'17'}
        />
    </svg>
);

export const ArrowRightIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <path d={'M5 12h14'} />

        <path d={'m12 5 7 7-7 7'} />
    </svg>
);

export const XIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <path d={'M18 6 6 18'} />

        <path d={'m6 6 12 12'} />
    </svg>
);

export const PowerIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <path d={'M12 2v10'} />

        <path d={'M18.4 6.6a9 9 0 1 1-12.77.04'} />
    </svg>
);

export const EyeIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <path
            d={
                'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0'
            }
        />

        <circle
            cx={'12'}
            cy={'12'}
            r={'3'}
        />
    </svg>
);

export const PencilIcon = ({ className }: IconProps) => (
    <svg {...base(className)}>
        <path
            d={
                'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z'
            }
        />

        <path d={'m15 5 4 4'} />
    </svg>
);

// The "attributes" hero glyph from the prototype (three stacked parallelograms).
export const AttributesIcon = ({ className }: IconProps) => (
    <svg
        className={className}
        viewBox={'0 0 36 36'}
        fill={'none'}
        stroke={'currentColor'}
        strokeWidth={1.8}
        strokeMiterlimit={10}
    >
        <path
            d={
                'M30 5.25H8.25C7.8 5.25 7.35 5.4 7.05 5.85L4.8 8.85C4.05 9.9 4.8 11.25 6 11.25H27.75C28.2 11.25 28.65 11.1 28.95 10.65L31.2 7.65C31.95 6.6 31.2 5.25 30 5.25Z'
            }
        />

        <path
            d={
                'M6 15H27.75C28.2 15 28.65 15.15 28.95 15.6L31.2 18.6C31.95 19.65 31.2 21 30 21H8.25C7.8 21 7.35 20.85 7.05 20.4L4.8 17.4C4.05 16.35 4.8 15 6 15Z'
            }
        />

        <path
            d={
                'M30 24.75H8.25C7.8 24.75 7.35 24.9 7.05 25.35L4.8 28.35C4.05 29.4 4.8 30.75 6 30.75H27.75C28.2 30.75 28.65 30.6 28.95 30.15L31.2 27.15C31.95 26.1 31.2 24.75 30 24.75Z'
            }
        />
    </svg>
);
